/* Cart promo sync — single-tier GWP progress bar + Surprise & Delight reveal
   ---------------------------------------------------------------------------
   All promo CONFIG (gift handles, threshold, copy) lives in snippets/cart-promo.liquid.
   The promo markup is rendered server-side with the correct state already applied:
     - tier progress bar: only rendered if the tier gift is published + in stock
     - S&D reveal: shown only when the S&D gift line is actually in the cart, and it
       then replaces the tier progress bar
   So every drawer re-render (add to cart, qty change, remove) is already correct.

   This file only has to:
     1. Re-check the cart shortly after any change, because the "Auto add to cart"
        app adds/removes gift lines a moment AFTER the theme has re-rendered. If the
        cart no longer matches what the drawer was rendered with, the drawer is
        refreshed so the gift line and the promo state appear without user action.
     2. Animate the S&D reveal only when it newly appears (not on every re-render).
     3. Push GA4 promotion events to the GTM dataLayer:
          view_promotion   — first time the S&D reveal shows (once per session)
          select_promotion — checkout clicked while the S&D reveal is showing */

const MB_RECHECK_DELAYS = [1200, 3500]; // ms after a cart change to re-check for app gift changes

let mbRecheckTimers = [];
let mbSdWasVisible = null; // null = not yet known (first paint never animates)

function mbPromoEl() {
  return document.getElementById('gwp-promo');
}

function mbCartSignature(cart) {
  if (!cart || !Array.isArray(cart.items)) return '';
  return cart.items.map(function (item) { return item.key + ':' + item.quantity; }).join(',');
}

/* ---- GA4 promotion tracking (pushed to GTM dataLayer) ---- */
function mbTrackPromo(eventName) {
  try {
    const promo = mbPromoEl();
    if (!promo) return;
    const d = promo.dataset;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null }); // clear the previous ecommerce object
    window.dataLayer.push({
      event: eventName,
      ecommerce: {
        creative_name: 'cart_drawer_surprise',
        promotion_id: d.sdPromoId,
        promotion_name: d.sdPromoName,
        items: [{
          item_id: d.sdSku,
          item_name: d.sdName,
          price: Number(d.sdValue) || 0,
          quantity: 1,
          promotion_id: d.sdPromoId,
          promotion_name: d.sdPromoName
        }]
      }
    });
  } catch (e) {}
}

function mbTrackReveal() {
  try {
    if (sessionStorage.getItem('mb_gwp_view_tracked')) return; // once per session
    sessionStorage.setItem('mb_gwp_view_tracked', '1');
  } catch (e) {}
  mbTrackPromo('view_promotion');
}

function mbSdVisible() {
  const el = document.getElementById('sd-reveal');
  return !!el && el.style.display !== 'none';
}

/* Runs after any drawer re-render (via MutationObserver). */
function mbAfterRender() {
  const visible = mbSdVisible();
  if (visible) {
    if (mbSdWasVisible === false) {
      const el = document.getElementById('sd-reveal');
      if (el) el.classList.add('sd-animate');
    }
    mbTrackReveal();
  }
  mbSdWasVisible = visible;
}

/* Re-fetch the drawer so it reflects gift lines the app added/removed after our render. */
function mbRefreshDrawer() {
  return fetch(`${routes.cart_url}?sections=cart-drawer,cart-icon-bubble`)
    .then(function (r) { return r.json(); })
    .then(function (sections) {
      if (sections['cart-drawer']) {
        const doc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
        ['#gwp-promo', 'cart-drawer-items', '.cart-drawer__footer'].forEach(function (selector) {
          const target = document.querySelector(selector);
          const source = doc.querySelector(selector);
          if (target && source) target.replaceWith(source);
        });
      }
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble && sections['cart-icon-bubble']) {
        const doc = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
        const source = doc.querySelector('.shopify-section');
        if (source) bubble.innerHTML = source.innerHTML;
      }
    });
}

function mbRecheck() {
  const promo = mbPromoEl();
  if (!promo) return;
  fetch('/cart.js')
    .then(function (r) { return r.json(); })
    .then(function (cart) {
      const current = mbPromoEl();
      if (!current) return;
      if (mbCartSignature(cart) === (current.dataset.cartSignature || '')) return;
      return mbRefreshDrawer();
    })
    .catch(function () {});
}

function mbScheduleRecheck() {
  mbRecheckTimers.forEach(clearTimeout);
  mbRecheckTimers = MB_RECHECK_DELAYS.map(function (ms) { return setTimeout(mbRecheck, ms); });
}

/* Kept for backwards-compat: older callers (and apps) may still call
   updateProgressBar(total). The promo state is server-rendered now, so this
   just schedules a re-check. */
function updateProgressBar() {
  mbScheduleRecheck();
}

/* Fire select_promotion when checkout is clicked while the reveal is showing.
   Delegated on document because the drawer footer re-renders on cart updates. */
document.addEventListener('click', function (e) {
  const btn = e.target.closest && e.target.closest('#CartDrawer-Checkout');
  if (!btn) return;
  if (mbSdVisible()) mbTrackPromo('select_promotion');
});

document.addEventListener('cart:updated', mbScheduleRecheck);

document.addEventListener('DOMContentLoaded', function () {
  const drawer = document.querySelector('cart-drawer');
  if (drawer && 'MutationObserver' in window) {
    let raf = null;
    new MutationObserver(function () {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(mbAfterRender);
    }).observe(drawer, { childList: true, subtree: true });
  }
  mbAfterRender();
  mbScheduleRecheck(); // catches a gift the app adds on page load
});

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement.getAttribute('name'),
      event.target.dataset.quantityVariantId
    );
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['#gwp-promo', 'cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
    mbScheduleRecheck();
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });
        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
        mbScheduleRecheck();
      })
      .catch(() => {
        this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').innerHTML = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading-overlay`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading-overlay`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'change',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
